import axios, { AxiosResponse } from "axios";
import { useMutation } from "react-query";

const apiBaseUrl =
  process.env.REACT_APP_API_BASEURL + "/BusinessLicensePermits";

type FormDataValue = string | number | boolean | null | undefined;

export interface BLPFileForDelete {
  businessLicensePermitFileId?: number;
  businessLicensePermitRecordId?: number;
  businessLicensePermitFileName?: string;
  createdDate?: string | null;
  createdBy?: string | null;
  modifiedDate?: string | null;
  modifiedBy?: string | null;
}

export interface UpdateBLPRequest {
  recordId: number;
  officeId?: number | null;
  issuer?: number | null;
  issuerName?: string | null;
  issuerPhone?: string | null;
  type?: number | null;
  licenseOrPermitNumber?: string | null;
  issuedDate?: string | null;
  renewedDate?: string | null;

  licenseStatus?: number | null;
  renewalStatus?: number | null;

  isLicenseRequired?: boolean | null;
  isOldLicense?: boolean | null;
  expirationDate?: string | null;
  notes?: string | null;
  attachments?: (File | null)[];
  attachmentsForDelete?: BLPFileForDelete[];
}

const appendIfDefined = (
  formData: FormData,
  key: string,
  value: FormDataValue,
) => {
  if (value !== null && value !== undefined && value !== "") {
    formData.append(key, String(value));
  }
};

const appendBoolean = (
  formData: FormData,
  key: string,
  value: boolean | null | undefined,
) => {
  if (value !== null && value !== undefined) {
    formData.append(key, value ? "true" : "false");
  }
};

const toNullableStatusId = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numericValue = Number(value);

  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return null;
  }

  return numericValue;
};

const appendAttachmentsForDelete = (
  formData: FormData,
  files: BLPFileForDelete[] | undefined,
) => {
  files?.forEach((file, index) => {
    const prefix = `attachmentsForDelete[${index}]`;

    appendIfDefined(
      formData,
      `${prefix}.businessLicensePermitFileId`,
      file.businessLicensePermitFileId,
    );

    appendIfDefined(
      formData,
      `${prefix}.businessLicensePermitRecordId`,
      file.businessLicensePermitRecordId,
    );

    appendIfDefined(
      formData,
      `${prefix}.businessLicensePermitFileName`,
      file.businessLicensePermitFileName,
    );

    appendIfDefined(formData, `${prefix}.createdDate`, file.createdDate);

    appendIfDefined(formData, `${prefix}.createdBy`, file.createdBy);

    appendIfDefined(formData, `${prefix}.modifiedDate`, file.modifiedDate);

    appendIfDefined(formData, `${prefix}.modifiedBy`, file.modifiedBy);
  });
};

const createUpdateBLPFormData = (updatedBLP: UpdateBLPRequest): FormData => {
  const formData = new FormData();

  formData.append("recordId", String(updatedBLP.recordId));

  updatedBLP.attachments?.forEach((file) => {
    if (file) {
      formData.append("attachments", file);
    }
  });

  appendAttachmentsForDelete(formData, updatedBLP.attachmentsForDelete);

  appendIfDefined(formData, "officeId", updatedBLP.officeId);

  appendIfDefined(formData, "issuer", updatedBLP.issuer);

  appendIfDefined(formData, "issuerName", updatedBLP.issuerName);

  appendIfDefined(formData, "issuerPhone", updatedBLP.issuerPhone);

  const typeId =
    updatedBLP.type !== null &&
    updatedBLP.type !== undefined &&
    Number(updatedBLP.type) > 0
      ? Number(updatedBLP.type)
      : null;

  appendIfDefined(formData, "type", typeId);

  appendIfDefined(
    formData,
    "licenseOrPermitNumber",
    updatedBLP.licenseOrPermitNumber,
  );

  appendIfDefined(formData, "issuedDate", updatedBLP.issuedDate);

  appendIfDefined(formData, "renewedDate", updatedBLP.renewedDate);

  const licenseStatusId = toNullableStatusId(updatedBLP.licenseStatus);

  const renewalStatusId = toNullableStatusId(updatedBLP.renewalStatus);

  appendIfDefined(formData, "licenseStatusId", licenseStatusId);

  appendIfDefined(formData, "renewalStatusId", renewalStatusId);

  appendBoolean(formData, "isLicenseRequired", updatedBLP.isLicenseRequired);

  appendBoolean(formData, "isOldLicense", updatedBLP.isOldLicense);

  appendIfDefined(formData, "expirationDate", updatedBLP.expirationDate);

  appendIfDefined(formData, "notes", updatedBLP.notes);

  return formData;
};

export const updateBLP = async (
  updatedBLP: UpdateBLPRequest,
): Promise<AxiosResponse<void>> => {
  const formData = createUpdateBLPFormData(updatedBLP);

  return axios.put<void>(`${apiBaseUrl}/${updatedBLP.recordId}`, formData);
};

export const useUpdateBLP = () => {
  return useMutation<AxiosResponse<void>, unknown, UpdateBLPRequest>(updateBLP);
};
